import { useRef, useState, useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Autocomplete, DirectionsRenderer } from '@react-google-maps/api';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FullscreenOutlinedIcon from '@mui/icons-material/FullscreenOutlined';
import FullscreenExitOutlinedIcon from '@mui/icons-material/FullscreenExitOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useMediaQuery } from 'react-responsive';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
import dayjs from 'dayjs';
import { Element } from 'react-scroll';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import { URL_PROXY } from './apiConfig';

/* global google */
const defaultCenter = { lat: 46.5569, lng: 11.7087 };
const libraries = ['places', 'routes'];
const bookingPhone = '+393429118790';
const bookingEmail = 'info@taximoroder.it';
const venicePricingArea = { center: { lat: 45.4408, lng: 12.3155 }, radiusKm: 20 };
const castelnuovoWaypoint = { lat: 46.05281, lng: 11.49329 };
const timeOptions = Array.from({ length: 96 }, (_, index) => {
  const hours = String(Math.floor(index / 4)).padStart(2, '0');
  const minutes = String((index % 4) * 15).padStart(2, '0');
  return `${hours}:${minutes}`;
});

const formatBookingDate = (value) => {
  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate.format('DD-MM-YYYY') : '';
};

const formatBookingTime = (value) => {
  if (typeof value === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
    return value;
  }

  const parsedTime = dayjs(value);
  return parsedTime.isValid() ? parsedTime.format('HH:mm') : '';
};

const distanceBetweenCoordinatesKm = (first, second) => {
  const earthRadiusKm = 6371;
  const toRadians = (degrees) => degrees * (Math.PI / 180);
  const latitudeDifference = toRadians(second.lat - first.lat);
  const longitudeDifference = toRadians(second.lng - first.lng);
  const haversine = Math.sin(latitudeDifference / 2) ** 2 +
    Math.cos(toRadians(first.lat)) * Math.cos(toRadians(second.lat)) *
    Math.sin(longitudeDifference / 2) ** 2;

  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
};

const isInsideArea = (coordinates, area) => coordinates &&
  distanceBetweenCoordinatesKm(coordinates, area.center) <= area.radiusKm;

const isSouthTyrolPlace = (place) => (place.address_components || []).some((component) => {
  if (!component.types.includes('administrative_area_level_2')) return false;
  const province = `${component.short_name || ''} ${component.long_name || ''}`.toLowerCase();
  return component.short_name?.toUpperCase() === 'BZ' ||
    province.includes('bolzano') || province.includes('bozen') ||
    province.includes('south tyrol') || province.includes('südtirol');
});

const Booking = ({ id }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 1000 });
  const DatePickerComponent = isMobile ? MobileDatePicker : DesktopDatePicker;

  // Map and directions state
  const [map, setMap] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [distance, setDistance] = useState('');
  const [duration, setDuration] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [price, setPrice] = useState(null);
  const [quoteToken, setQuoteToken] = useState('');
  
  // Origin/Destination addresses and coordinates
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [originInput, setOriginInput] = useState('');
  const [destinationInput, setDestinationInput] = useState('');
  const [originCoordinates, setOriginCoordinates] = useState(null);
  const [destinationCoordinates, setDestinationCoordinates] = useState(null);
  const [originIsSouthTyrol, setOriginIsSouthTyrol] = useState(false);
  const [destinationIsSouthTyrol, setDestinationIsSouthTyrol] = useState(false);
  
  // Date and time state
  const [selectedDate, setSelectedDate] = useState(null); 
  const minDate = dayjs().add(2, 'day');
  const [selectedTime, setSelectedTime] = useState(null);
  
  const [elevationGain, setElevationGain] = useState(0);
  
  // Loading and error states
  const [isLoadingMap, setIsLoadingMap] = useState(true);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [priceError, setPriceError] = useState('');
  const [dateIsEmpty, setDateIsEmpty] = useState(false);
  const [timeIsEmpty, setTimeIsEmpty] = useState(false);
  const [persons, setPersons] = useState(1);
  const [bookingEnabled, setBookingEnabled] = useState(true);
  
  // Refs for Autocomplete inputs and marker instances
  const originRef = useRef(null);
  const destiantionRef = useRef(null);
  const originMarkerRef = useRef(null);
  const destinationMarkerRef = useRef(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${URL_PROXY}/settings/public`)
      .then((response) => setBookingEnabled(response.data.bookingEnabled !== false))
      .catch(() => setBookingEnabled(true));
  }, []);
  
  const handleBookingNavigation = () => {
    const normalizedDate = formatBookingDate(selectedDate);
    const normalizedTime = formatBookingTime(selectedTime);
    const bookingData = {
      price: price,
      OriginAdress: origin,
      DestinationAdress: destination,
      Persons: persons,
      Time: normalizedTime,
      Date: normalizedDate,
      quoteToken
    };
    navigate('/booking', { state: bookingData });
  };
  
  // Load Google Maps API
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries: libraries,
  });
  
  const fetchElevationData = async (path) => {
    const elevationService = new google.maps.ElevationService();
    const elevationRequest = {
      path: path,
      samples: 16
    };
  
    return new Promise((resolve, reject) => {
      elevationService.getElevationAlongPath(elevationRequest, (result, status) => {
        if (status !== google.maps.ElevationStatus.OK) {
          reject('Elevation service failed due to: ' + status);
          return;
        }
        resolve(result);
      });
    });
  };
  
  const calculateTotalElevationGain = (elevations) => {
    let totalGain = 0;
    let totalLoss = 0;
    for (let i = 1; i < elevations.length; i++) {
      const elevationDifference = elevations[i].elevation - elevations[i - 1].elevation;
      if (elevationDifference > 0) {
        totalGain += elevationDifference;
      } else {
        totalLoss += Math.abs(elevationDifference);
      }
    }
    return totalGain + totalLoss;
  };
  
  // Calculate and display the route (and elevation)
  const setRoute = useCallback(async () => {
    if (!origin || !destination || !originCoordinates || !destinationCoordinates || !isLoaded) return;
  
    try {
      const directionsService = new google.maps.DirectionsService();
      const standardRouteRequest = {
        origin: origin,
        destination: destination,
        travelMode: google.maps.TravelMode.DRIVING,
      };
      const isVeniceToSouthTyrol =
        (isInsideArea(originCoordinates, venicePricingArea) && destinationIsSouthTyrol) ||
        (isInsideArea(destinationCoordinates, venicePricingArea) && originIsSouthTyrol);
      const preferredRouteRequest = isVeniceToSouthTyrol
        ? {
            ...standardRouteRequest,
            waypoints: [{ location: castelnuovoWaypoint, stopover: false }],
          }
        : standardRouteRequest;

      let results;
      try {
        results = await directionsService.route(preferredRouteRequest);
      } catch (preferredRouteError) {
        if (!isVeniceToSouthTyrol) throw preferredRouteError;
        console.warn('Route through Castelnuovo unavailable; using Google default route.', preferredRouteError);
        results = await directionsService.route(standardRouteRequest);
      }

      const selectedRoute = results.routes[0];
      const totalDistanceMeters = selectedRoute.legs.reduce(
        (total, leg) => total + (leg.distance?.value || 0),
        0
      );
      const totalDurationSeconds = selectedRoute.legs.reduce(
        (total, leg) => total + (leg.duration?.value || 0),
        0
      );
      const totalDurationMinutes = Math.round(totalDurationSeconds / 60);
      const durationHours = Math.floor(totalDurationMinutes / 60);
      const durationMinutes = totalDurationMinutes % 60;
  
      setDirectionsResponse(results);
      setDistance(`${Math.round(totalDistanceMeters / 1000)} km`);
      setDuration(`${durationHours ? `${durationHours} h ` : ''}${durationMinutes} min`);
  
      // Calculate elevation gain
      const path = selectedRoute.overview_path;
      const elevationResults = await fetchElevationData(path);
      const totalElevationGain = calculateTotalElevationGain(elevationResults);
      setElevationGain(totalElevationGain);
  
      // Use the route's bounds to update center and fit bounds
      if (map && selectedRoute.bounds) {
        const routeBounds = selectedRoute.bounds;
        setMapCenter(routeBounds.getCenter().toJSON());
        map.fitBounds(routeBounds);
      }
    } catch (error) {
      console.error("Failed to calculate route:", error);
      alert('Something went wrong. Please try different locations.');
    }
  }, [origin, destination, originCoordinates, destinationCoordinates, originIsSouthTyrol, destinationIsSouthTyrol, isLoaded, map]);
  
  useEffect(() => {
    if (origin && destination && originCoordinates && destinationCoordinates && isLoaded) {
      setRoute();
    }
  }, [origin, destination, originCoordinates, destinationCoordinates, isLoaded, setRoute]);
  
  // Setup Autocomplete listeners and extract coordinates
  useEffect(() => {
    if (isLoaded && window.google) {
      const centralLocation = new window.google.maps.LatLng(defaultCenter.lat, defaultCenter.lng);
      const options = {
        location: centralLocation,
        radius: 2000000,
      };
  
      const originAutocomplete = new window.google.maps.places.Autocomplete(originRef.current, options);
      originAutocomplete.addListener('place_changed', () => {
        const place = originAutocomplete.getPlace();
        const selectedAddress = place.formatted_address || '';
        setOrigin(selectedAddress);
        setOriginInput(selectedAddress);
        setOriginIsSouthTyrol(isSouthTyrolPlace(place));
        if (place.geometry && place.geometry.location) {
          setOriginCoordinates({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        }
        setShowResult(false);
      });
  
      const destinationAutocomplete = new window.google.maps.places.Autocomplete(destiantionRef.current, options);
      destinationAutocomplete.addListener('place_changed', () => {
        const place = destinationAutocomplete.getPlace();
        const selectedAddress = place.formatted_address || '';
        setDestination(selectedAddress);
        setDestinationInput(selectedAddress);
        setDestinationIsSouthTyrol(isSouthTyrolPlace(place));
        if (place.geometry && place.geometry.location) {
          setDestinationCoordinates({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        }
        setShowResult(false);
      });
  
      return () => {
        google.maps.event.clearListeners(originAutocomplete, 'place_changed');
        google.maps.event.clearListeners(destinationAutocomplete, 'place_changed');
      };
    }
  }, [isLoaded]);
  
  // Drop a custom marker for the origin (Marker A)
  useEffect(() => {
    if (map && originCoordinates) {
      if (originMarkerRef.current) {
        originMarkerRef.current.setMap(null);
      }
      originMarkerRef.current = new google.maps.Marker({
        position: originCoordinates,
        map: map,
        title: 'Custom Marker A: Origin',
        label: {
          text: 'Start',
          color: 'white',
          fontSize: '10px',
          fontWeight: 'bold'
        }
      });
      // Zoom in on the origin if destination is not yet set
      if (!destinationCoordinates) {
        map.setCenter(originCoordinates);
        map.setZoom(15);
      }
    }
  }, [originCoordinates, map, destinationCoordinates]);
  
  // Drop a custom marker for the destination (Marker B)
  useEffect(() => {
    if (map && destinationCoordinates) {
      if (destinationMarkerRef.current) {
        destinationMarkerRef.current.setMap(null);
      }
      destinationMarkerRef.current = new google.maps.Marker({
        position: destinationCoordinates,
        map: map,
        title: 'Custom Marker B: Destination',
        label: {
          text: 'End',
          color: 'white',
          fontSize: '10px',
          fontWeight: 'bold'
        }
      });
      // Zoom in on the destination if origin is not yet set
      if (!originCoordinates) {
        map.setCenter(destinationCoordinates);
        map.setZoom(15);
      }
    }
  }, [destinationCoordinates, map, originCoordinates]);
  
  useEffect(() => {
    setShowResult(false);
  }, [persons, selectedDate, selectedTime]);

  useEffect(() => {
    document.body.style.overflow = mapFullscreen ? 'hidden' : '';

    const resizeTimer = window.setTimeout(() => {
      if (map && window.google) {
        google.maps.event.trigger(map, 'resize');
        map.setCenter(mapCenter);
      }
    }, 220);

    return () => {
      window.clearTimeout(resizeTimer);
      document.body.style.overflow = '';
    };
  }, [mapFullscreen, map, mapCenter]);
  
  if (!isLoaded) {
    return <CircularProgress />;
  }
  
  function handleResetInputs() {
    setSelectedDate(null);
    setSelectedTime(null);
    setShowResult(false);
    setPrice(null);
    setPriceError('');
    setOrigin('');
    setDestination('');
    setOriginInput('');
    setDestinationInput('');
    setOriginCoordinates(null);
    setDestinationCoordinates(null);
    setOriginIsSouthTyrol(false);
    setDestinationIsSouthTyrol(false);
    setDateIsEmpty(false);
    setTimeIsEmpty(false);
    setPersons(1);
  
    // Clear route and markers
    clearRoute();
    if (originMarkerRef.current) originMarkerRef.current.setMap(null);
    if (destinationMarkerRef.current) destinationMarkerRef.current.setMap(null);
    if (originRef.current) originRef.current.value = '';
    if (destiantionRef.current) destiantionRef.current.value = '';
  }
  
  function calculatePrice() {
    if (!bookingEnabled) return;
    setIsLoadingPrice(true);
    setLoadingMessage(t('booking_loadingMessage'));
    setPriceError('');
    setDateIsEmpty(false);
    setTimeIsEmpty(false);
  
    const normalizedDate = formatBookingDate(selectedDate);
    const normalizedTime = formatBookingTime(selectedTime);

    if (!normalizedDate) {
      setDateIsEmpty(true);
    }
  
    if (!normalizedTime) {
      setTimeIsEmpty(true);
    }
  
    if (!normalizedDate || !normalizedTime) {
      setIsLoadingPrice(false);
      return;
    }
  
    if (!distance || distance === '') {
      console.log("Distance is required.");
      alert('Please Select Addresses from the Dropdown!');
      setIsLoadingPrice(false);
      return;
    }
  
    if (!duration || duration === '') {
      console.log("Duration is required.");
      alert('Please Select Addresses from the Dropdown!');
      setIsLoadingPrice(false);
      return;
    }
  
    console.log("Distance", distance);
    console.log("Duration", duration);
    console.log("Persons", persons);
    console.log("Destination", destination);
    console.log("Origin", origin);
    console.log("Date", normalizedDate);
    console.log("Time", normalizedTime);
  
    const payload = {
      distance: distance,
      time: normalizedTime,
      date: normalizedDate,
      elevationGain: elevationGain,
      persons: persons,
      origin,
      destination,
      originCoordinates,
      destinationCoordinates
    };
  
    console.log(payload);
  
    axios.post(`${URL_PROXY}/requestPrice`, payload)
      .then(response => {
        const calculatedPrice = response.data.price;
        setPrice(calculatedPrice);
        setQuoteToken(response.data.quoteToken || '');
        setShowResult(true);
        console.log("Calculated Price:", calculatedPrice);
      })
      .catch(error => {
        const serverMessage = error.response?.data?.error || error.response?.data?.message;
        const errorMessage = serverMessage || (error.request ? t('booking_serverUnavailable') : error.message);
        setPriceError(errorMessage);
        console.error('Price calculation failed:', {
          status: error.response?.status,
          message: errorMessage,
          response: error.response?.data,
        });
      })
      .finally(() => {
        setIsLoadingPrice(false);
      });
  }
  
  function clearRoute() {
    setDirectionsResponse(null);
    setDistance('');
    setDuration('');
  }
  
  const handleChange = (event) => {
    setPersons(event.target.value);
  };

  const invalidateRouteSelection = (field, inputValue) => {
    if (field === 'origin') {
      setOriginInput(inputValue);
      setOrigin('');
      setOriginCoordinates(null);
      setOriginIsSouthTyrol(false);
      if (originMarkerRef.current) {
        originMarkerRef.current.setMap(null);
        originMarkerRef.current = null;
      }
    } else {
      setDestinationInput(inputValue);
      setDestination('');
      setDestinationCoordinates(null);
      setDestinationIsSouthTyrol(false);
      if (destinationMarkerRef.current) {
        destinationMarkerRef.current.setMap(null);
        destinationMarkerRef.current = null;
      }
    }

    clearRoute();
    setShowResult(false);
    setPrice(null);
    setPriceError('');
  };
  
  return (
    <Element name={id}>
      <section className="bookingSection" aria-labelledby="booking-title">
        <div className="bookingCard">
          <header className="bookingHeader">
            <p className="bookingEyebrow">{t('booking_booking')}</p>
            <span className={`bookingStatus${bookingEnabled ? '' : ' bookingStatusDisabled'}`}><i aria-hidden="true" />{bookingEnabled ? t('booking_available') : t('booking_unavailable')}</span>
          </header>

          <div className="bookingIntroduction">
            <h2 id="booking-title">{t('booking_bookYourRide')}</h2>
            <p>{t('booking_intro')}</p>
          </div>

          <div className="bookingLayout">
            <div className="bookingForm">
              <div className="bookingField bookingFieldFull">
                <label htmlFor="booking-origin">{t('booking_start')}</label>
                <Autocomplete>
                  <input
                    className="bookingTextInput"
                    id="booking-origin"
                    ref={originRef}
                    value={originInput}
                    onChange={(event) => invalidateRouteSelection('origin', event.target.value)}
                    placeholder={t('booking_enterLocation')}
                  />
                </Autocomplete>
              </div>

              <div className="bookingField bookingFieldFull">
                <label htmlFor="booking-destination">{t('booking_destination')}</label>
                <Autocomplete>
                  <input
                    className="bookingTextInput"
                    id="booking-destination"
                    ref={destiantionRef}
                    value={destinationInput}
                    onChange={(event) => invalidateRouteSelection('destination', event.target.value)}
                    placeholder={t('booking_enterDestination')}
                  />
                </Autocomplete>
              </div>

              <div className="bookingField bookingFieldCompact">
                <label htmlFor="PersonsSelect">{t('booking_persons')}</label>
                <TextField
                  id="PersonsSelect"
                  select
                  fullWidth
                  value={persons}
                  onChange={handleChange}
                  SelectProps={{ MenuProps: { PaperProps: { className: 'bookingPersonsMenu' } } }}
                >
                  {[1, 2, 3, 4, 5, 6, 7].map((number) => (
                    <MenuItem key={number} value={number}>{number}</MenuItem>
                  ))}
                </TextField>
              </div>

              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={(i18n.resolvedLanguage || 'en').slice(0, 2)}>
                <div className="bookingField bookingFieldCompact bookingDateField">
                  <label>{t('booking_date')}</label>
                  <DatePickerComponent
                    value={selectedDate}
                    onChange={setSelectedDate}
                    minDate={minDate}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: { error: dateIsEmpty, fullWidth: true },
                      ...(isMobile ? {
                        mobilePaper: { className: 'bookingPickerDialog' },
                        dialog: { disableScrollLock: true },
                      } : {}),
                    }}
                  />
                </div>

                <div className="bookingField bookingFieldCompact bookingTimeField">
                  <label>{t('booking_time')}</label>
                  {isMobile ? (
                    <TextField
                      select
                      fullWidth
                      error={timeIsEmpty}
                      value={selectedTime ? dayjs(selectedTime).format('HH:mm') : ''}
                      onChange={(event) => setSelectedTime(dayjs(`2000-01-01T${event.target.value}`))}
                      SelectProps={{
                        displayEmpty: true,
                        renderValue: (value) => value || t('booking_selectTime'),
                        MenuProps: { PaperProps: { className: 'bookingTimeMenu' } },
                      }}
                    >
                      {timeOptions.map((time) => <MenuItem key={time} value={time}>{time}</MenuItem>)}
                    </TextField>
                  ) : (
                    <DesktopTimePicker
                      value={selectedTime}
                      onChange={setSelectedTime}
                      ampm={false}
                      minutesStep={15}
                      format="HH:mm"
                      slotProps={{ textField: { error: timeIsEmpty, fullWidth: true } }}
                    />
                  )}
                </div>
              </LocalizationProvider>

              <div className="bookingActions">
                <button type="button" className="bookingReset" onClick={handleResetInputs}>{t('booking_reset')}</button>
                <button type="button" className="bookingCalculate" onClick={calculatePrice} disabled={isLoadingPrice || !bookingEnabled}>
                  {isLoadingPrice ? t('booking_calculating') : t('booking_calculatePrice')}
                </button>
              </div>

              {!bookingEnabled && (
                <div className="bookingDisabledNotice" role="status">
                  <div className="bookingDisabledCopy">
                    <strong>{t('booking_unavailable')}</strong>
                    <span>{t('booking_unavailable_note')}</span>
                  </div>
                  <div className="bookingDisabledContacts">
                    <a href={`tel:${bookingPhone}`}><PhoneOutlinedIcon aria-hidden="true" />{t('contact_call')}</a>
                    <a href={`https://wa.me/${bookingPhone.replace('+', '')}`} target="_blank" rel="noopener noreferrer"><WhatsAppIcon aria-hidden="true" />WhatsApp</a>
                    <a href={`mailto:${bookingEmail}`}><EmailOutlinedIcon aria-hidden="true" />{t('contact_email')}</a>
                  </div>
                </div>
              )}

              {isLoadingPrice && <div className="bookingLoading"><CircularProgress size={24} /><span>{loadingMessage}</span></div>}
              {priceError && <div className="bookingPriceError" role="alert">{priceError}</div>}

              {showResult && price !== null && (
                <div className="bookingResult">
                  <div><small>{t('booking_price')}</small><strong>{price}€</strong></div>
                  <div><small>{t('booking_distance')}</small><span>{distance}</span></div>
                  <div><small>{t('booking_duration')}</small><span>{duration}</span></div>
                  <button type="button" onClick={handleBookingNavigation}>{t('booking_book')}</button>
                </div>
              )}
            </div>

            <div className={`bookingMap${mapFullscreen ? ' bookingMapFullscreen' : ''}`}>
            <GoogleMap
              center={mapCenter}
              zoom={8}
              mapContainerClassName="bookingMapCanvas"
              options={{
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: !isMobile,
              }}
              onLoad={(mapInstance) => {
                setMap(mapInstance);
                setIsLoadingMap(false);
              }}
            >
              {!isLoadingMap && directionsResponse && (
                <DirectionsRenderer 
                  directions={directionsResponse}
                  options={{ suppressMarkers: true }}
                />
              )}
            </GoogleMap>
            {isLoadingMap && <div className="bookingMapLoading"><CircularProgress /></div>}
            <button
              type="button"
              className="bookingMapFullscreenButton"
              aria-label={mapFullscreen ? t('booking_exitFullscreen') : t('booking_openFullscreen')}
              onClick={() => setMapFullscreen((fullscreen) => !fullscreen)}
            >
              {mapFullscreen ? <FullscreenExitOutlinedIcon /> : <FullscreenOutlinedIcon />}
              <span>{mapFullscreen ? t('booking_exitMap') : t('booking_fullscreenMap')}</span>
            </button>
            <div className="bookingMapLabel">{t('booking_mapLabel')}</div>
            </div>
          </div>
        </div>
      </section>
    </Element>
  );
};

export default Booking;
